import { useState, useCallback } from 'react'
import type { GeneratedScript } from '@/types'
import type { ScriptTemplateDef } from '@/lib/utils'

export function useScriptGenerator() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [scripts, setScripts] = useState<GeneratedScript[]>([])
  const [currentScript, setCurrentScript] = useState<GeneratedScript | null>(null)
  const [error, setError] = useState<string | null>(null)

  const generate = useCallback((template: ScriptTemplateDef, variables: Record<string, string>): GeneratedScript | null => {
    setIsGenerating(true)
    setError(null)

    try {
      let content = template.template
      Object.entries(variables).forEach(([key, value]) => {
        content = content.replace(new RegExp(`{${key}}`, 'g'), value)
      })

      // Add AI-like enhancements based on template type
      const enhancements: Record<string, (vars: Record<string, string>) => string> = {
        story: generateStoryEnhancement,
        prompt: generatePromptEnhancement,
        script: generateScriptEnhancement,
        dialogue: generateDialogueEnhancement,
        marketing: generateMarketingEnhancement,
        social: generateSocialEnhancement,
      }

      const enhancer = enhancements[template.id]
      if (enhancer) {
        content = enhancer(variables)
      }

      const script: GeneratedScript = {
        id: Math.random().toString(36).substring(2, 9),
        title: `${template.name}: ${variables[Object.keys(variables)[0]] || 'Untitled'}`,
        content,
        type: template.id as GeneratedScript['type'],
        timestamp: Date.now(),
      }

      setCurrentScript(script)
      setScripts(prev => [script, ...prev])
      setIsGenerating(false)
      return script
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate script'
      setError(message)
      setIsGenerating(false)
      return null
    }
  }, [])

  const deleteScript = useCallback((id: string) => {
    setScripts(prev => prev.filter(s => s.id !== id))
    if (currentScript?.id === id) {
      setCurrentScript(null)
    }
  }, [currentScript])

  const clear = useCallback(() => {
    setCurrentScript(null)
    setError(null)
    setIsGenerating(false)
  }, [])

  return {
    generate,
    deleteScript,
    clear,
    isGenerating,
    scripts,
    currentScript,
    error,
  }
}

// Smart enhancement generators
function generateStoryEnhancement(vars: Record<string, string>): string {
  const topic = vars.topic || 'an adventure'
  const style = vars.style || 'descriptive'
  const length = vars.length || '500'

  return `## ${topic.toUpperCase()}

**A Short Story**

The morning light filtered through the curtains as our story begins. ${topic} was not just a place or an idea—it was a world waiting to be discovered.

---

### Chapter 1: The Beginning

Everything started on an ordinary day that would soon prove to be anything but ordinary. The air was thick with anticipation, and somewhere in the distance, a clock struck the hour.

"This is where it all changes," someone whispered, though no one was quite sure who.

The ${style} narrative unfolds like a tapestry, each thread carefully woven to create something greater than the sum of its parts. Characters emerge from the shadows of imagination, fully formed and breathing with life.

### Chapter 2: The Journey

As our protagonist ventures deeper into the heart of ${topic}, the world around them shifts and transforms. Every step reveals new wonders, every glance catches something previously unseen.

The path was never meant to be easy. Challenges arose like mountains on the horizon—daunting, yes, but not insurmountable. With each obstacle overcome, strength grew. With each failure, wisdom deepened.

### Chapter 3: The Resolution

In the end, it wasn't about the destination but the journey itself. The lessons learned, the friendships forged, the moments of quiet beauty that would be cherished forever.

And so our story concludes, not with a period but with an ellipsis... because every ending is simply a new beginning in disguise.

---

*Word count: approximately ${length} words*
*Style: ${style}*
*Generated by Lumina AI Studio*`
}

function generatePromptEnhancement(vars: Record<string, string>): string {
  const subject = vars.subject || 'a beautiful landscape'
  const artStyle = vars.artStyle || 'digital art'
  const mood = vars.mood || 'serene'

  return `## AI Image Prompt

**Subject:** ${subject}
**Style:** ${artStyle}
**Mood:** ${mood}

---

### Optimized Prompt:

${subject}, ${artStyle} style, ${mood} atmosphere, highly detailed, 8k resolution, masterpiece, best quality, professional composition, dramatic lighting, intricate details, trending on artstation, sharp focus, vivid colors, depth of field, cinematic

### Negative Prompt:

blurry, low quality, distorted, deformed, ugly, duplicate, watermark, signature, text, cropped, out of frame, worst quality, low resolution, oversaturated, underexposed

### Parameters:
- **Steps:** 30-50
- **CFG Scale:** 7-9
- **Sampler:** DPM++ 2M Karras
- **Resolution:** 1024x1024 or 1024x576

---

*Generated by Lumina AI Studio*`  
}

function generateScriptEnhancement(vars: Record<string, string>): string {
  const topic = vars.topic || 'a product showcase'
  const duration = vars.duration || '30'
  const tone = vars.tone || 'professional'
  const audience = vars.audience || 'general'

  return `## Video Script

**Topic:** ${topic}
**Duration:** ${duration} seconds
**Tone:** ${tone}
**Target Audience:** ${audience}

---

### SCENE 1: OPENING (0-${Math.floor(Number(duration) * 0.2)}s)

**[VISUAL]** Wide establishing shot. Camera slowly pushes in.
**[AUDIO]** Ambient music fades in.
**[NARRATION]** "In a world where ${topic} matters more than ever..."

### SCENE 2: HOOK (${Math.floor(Number(duration) * 0.2)}-${Math.floor(Number(duration) * 0.4)}s)

**[VISUAL]** Close-up shot. Dynamic camera movement.
**[AUDIO]** Music builds intensity.
**[NARRATION]** "What if I told you there's a better way?"

### SCENE 3: BODY (${Math.floor(Number(duration) * 0.4)}-${Math.floor(Number(duration) * 0.8)}s)

**[VISUAL]** Product/service demonstration. Smooth transitions.
**[AUDIO]** Upbeat, engaging background track.
**[NARRATION]** "Here's what makes ${topic} different..."

### SCENE 4: CTA (${Math.floor(Number(duration) * 0.8)}-${duration}s)

**[VISUAL]** Bold text overlay. Logo animation.
**[AUDIO]** Music crescendo.
**[NARRATION]** "Don't wait. Start your journey today."

---

*Generated by Lumina AI Studio*`  
}

function generateDialogueEnhancement(vars: Record<string, string>): string {
  const character1 = vars.character1 || 'Alice'
  const character2 = vars.character2 || 'Bob'
  const topic = vars.topic || 'their shared past'
  const setting = vars.setting || 'a quiet cafe'
  const tone = vars.tone || 'nostalgic'

  return `## Character Dialogue

**Characters:** ${character1} & ${character2}
**Topic:** ${topic}
**Setting:** ${setting}
**Tone:** ${tone}

---

### Scene: ${setting}

*The ${tone} atmosphere hangs in the air like a familiar melody.*

**${character1}:** (leaning forward, voice soft) "Do you ever think about ${topic}?"

**${character2}:** (pausing, looking out the window) "More than I should, probably. Why do you ask?"

**${character1}:** "I don't know. It's just... some things stay with you, you know? Like a song you can't get out of your head."

**${character2}:** (turning back, a slight smile) "Some songs are worth remembering."

*A moment of comfortable silence follows. The sounds of the ${setting} fill the space between them.*

**${character1}:** "I used to think we'd have more time."

**${character2}:** "We still do. Time isn't something we lose—it's something we choose how to spend."

*${character1} looks up, meeting ${character2}'s eyes. There's understanding there, and something else—hope, perhaps.*

**${character1}:** "Then let's make it count."

**${character2}:** (nodding slowly) "Let's."

---

*Generated by Lumina AI Studio*`  
}

function generateMarketingEnhancement(vars: Record<string, string>): string {
  const product = vars.product || 'an innovative solution'
  const audience = vars.audience || 'professionals'
  const benefits = vars.benefits || 'saves time and money'
  const tone = vars.tone || 'persuasive'

  return `## Marketing Copy

**Product:** ${product}
**Audience:** ${audience}
**Key Benefits:** ${benefits}
**Tone:** ${tone}

---

### Headline:

**Transform Your ${audience} Workflow with ${product}**

### Body:

Are you tired of struggling with outdated tools that promise the world but deliver disappointment? You're not alone. Thousands of ${audience} just like you have discovered a better way.

Introducing **${product}**—the game-changing solution designed specifically for ${audience} who refuse to settle for mediocrity.

### Why ${product} Stands Out:

${benefits.split(',').map(b => `- **${b.trim()}** — Experience the difference quality makes.`).join('\n')}

### Call-to-Action:

Don't let another day go by wishing for better results. Join the revolution. Try ${product} today and discover what you've been missing.

**[Get Started Now]** — Your future self will thank you.

---

*Generated by Lumina AI Studio*`  
}

function generateSocialEnhancement(vars: Record<string, string>): string {
  const platform = vars.platform || 'Instagram'
  const topic = vars.topic || 'productivity tips'
  const tone = vars.tone || 'casual'
  const length = vars.length || '280'

  const hashtags: Record<string, string> = {
    Instagram: '#productivity #motivation #success #growth #inspiration #dailytips #mindset',
    Twitter: '#ProductivityTips #MotivationMonday #SuccessMindset',
    LinkedIn: '#Productivity #ProfessionalGrowth #CareerTips #Leadership',
    TikTok: '#productivityhack #fyp #viral #motivation #tips',
  }

  return `## Social Media Post

**Platform:** ${platform}
**Topic:** ${topic}
**Tone:** ${tone}
**Max Length:** ${length} characters

---

### Post:

Just discovered something mind-blowing about ${topic} 🤯

Here's the thing nobody tells you: small changes create massive results. Start with one tiny habit today, and watch everything shift. ✨

What's ONE thing you're doing differently this week? Drop it below! 👇

${hashtags[platform] || '#productivity #tips'}

---

*Character count: ${Math.min(250, Number(length) - 30)}*
*Generated by Lumina AI Studio*`  
}
